import { Popover, PopoverDisclosure, PopoverProvider } from "@ariakit/react";
import dayjs from "dayjs";
import { type PropsWithChildren, useState } from "react";
import CTA from "../CTA";
import MonthPicker from "../MonthPicker";
import { parseDate } from "../MonthPicker/MonthPicker";
import * as styles from "./MonthRangePicker.css";
import type { Props } from "./types";

const monthNames = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

export default function MonthRangePicker({
	children,
	availableDates,
	startDate,
	endDate,
	open,
	disclosureRef,
	selectRef,
	applyButtonRef,
	onOpen,
	onSelect,
}: PropsWithChildren<Props>) {
	const dateToMonthYearTuple = (d: Date) =>
		[monthNames[d.getMonth()] as string, `${d.getFullYear()}`] as [
			string,
			string,
		];
	const [start, setStart] = useState<[string, string] | undefined>(
		startDate ? dateToMonthYearTuple(startDate) : undefined,
	);
	const [end, setEnd] = useState<[string, string] | undefined>(
		endDate ? dateToMonthYearTuple(endDate) : undefined,
	);
	const availableMonths = groupDatesByYear(availableDates);
	const latestAvailableDate = parseDate(
		`${availableMonths.at(0)?.[0]} ${availableMonths.at(0)?.[1].at(-1)}`,
	);
	const earliestAvailableDate = parseDate(
		`${availableMonths.at(-1)?.[0]} ${availableMonths.at(-1)?.[1][0]}`,
	);
	const availableMonthsStart = end
		? filterTuplesByDate(availableMonths, end[0], end[1], "<")
		: availableMonths;
	const availableMonthsEnd = start
		? filterTuplesByDate(availableMonths, start[0], start[1], ">")
		: availableMonths;

	const handleSelectStart = (d: Date) => {
		const year = d.getFullYear();
		const month = monthNames[d.getMonth()];
		setStart([month, `${year}`]);
	};
	const handleSelectEnd = (d: Date) => {
		const year = d.getFullYear();
		const month = monthNames[d.getMonth()];
		setEnd([month, `${year}`]);
	};
	const handleApplyRange = () => {
		console.log(start, end);
		if (start && end) {
			const startDate = parseDate(`${start[0]} ${start[1]}`);
			const endDate = parseDate(`${end[0]} ${end[1]}`);

			onSelect?.(
				dayjs(startDate).startOf("month").toDate(),
				dayjs(endDate).endOf("month").toDate(),
			);
			onOpen?.(false);
		}
	};
	return (
		<PopoverProvider placement="bottom-end" open={open} setOpen={onOpen}>
			<PopoverDisclosure
				render={(props) => (
					<div
						{...props}
						className={`${open ? "open" : ""}`}
						ref={disclosureRef}
					>
						{children}
					</div>
				)}
			/>
			<Popover className={styles.popover}>
				<div className={styles.range} ref={selectRef}>
					<div className={styles.monthPickerWrap}>
						<span className={styles.label}>Select period start</span>
						<div className={styles.monthPicker}>
							<MonthPicker
								key={JSON.stringify(end)}
								label=""
								hideButton
								availableMonths={availableMonthsStart}
								onChange={handleSelectStart}
								value={
									start
										? parseDate(`${start[0]} ${start[1]}`)
										: earliestAvailableDate
								}
							/>
						</div>
					</div>
					<div className={styles.monthPickerWrap}>
						<span className={styles.label}>Select period end</span>
						<div className={styles.monthPicker}>
							<MonthPicker
								key={JSON.stringify(start)}
								label=""
								hideButton
								availableMonths={availableMonthsEnd}
								onChange={handleSelectEnd}
								value={
									end ? parseDate(`${end[0]} ${end[1]}`) : latestAvailableDate
								}
							/>
						</div>
					</div>
				</div>
				<CTA
					size="m"
					disabled={!start && !end}
					onClick={handleApplyRange}
					ref={applyButtonRef}
				>
					Apply
				</CTA>
			</Popover>
		</PopoverProvider>
	);
}

function groupDatesByYear(dates: Date[]): [string, string[]][] {
	const yearMap: { [year: number]: Set<string> } = {};

	for (const date of dates) {
		const year = date.getFullYear();
		const month = monthNames[date.getMonth()];
		if (!yearMap[year]) {
			yearMap[year] = new Set();
		}
		yearMap[year].add(month);
	}

	const result: [number, string[]][] = Object.keys(yearMap).map((yearStr) => {
		const year = Number.parseInt(yearStr, 10);
		return [year, Array.from(yearMap[year])];
	});

	result.sort((a, b) => b[0] - a[0]);
	return result.map(([year, months]) => [`${year}`, months]);
}

function filterTuplesByDate(
	data: [string, string[]][],
	pivotMonth: string,
	pivotYear: string,
	operator: ">" | "<",
): [string, string[]][] {
	// Define the ordering for month abbreviations
	const monthOrder: { [month: string]: number } = {
		Jan: 1,
		Feb: 2,
		Mar: 3,
		Apr: 4,
		May: 5,
		Jun: 6,
		Jul: 7,
		Aug: 8,
		Sep: 9,
		Oct: 10,
		Nov: 11,
		Dec: 12,
	};

	if (!(pivotMonth in monthOrder)) {
		throw new Error(`Invalid pivot month: ${pivotMonth}`);
	}

	return (
		data
			.map(([year, months]) => {
				const _year = Number.parseInt(year, 10);
				const _pivotYear = Number.parseInt(pivotYear, 10);
				let filteredMonths: string[] = [];

				if (operator === ">") {
					if (_year > _pivotYear) {
						// For years greater than the pivot year, all months qualify.
						filteredMonths = months;
					} else if (year === pivotYear) {
						// For the same year, only include months after the pivot month.
						filteredMonths = months.filter(
							(month) => monthOrder[month] > monthOrder[pivotMonth],
						);
					}
				} else if (operator === "<") {
					if (_year < _pivotYear) {
						// For years less than the pivot year, all months qualify.
						filteredMonths = months;
					} else if (year === pivotYear) {
						// For the same year, only include months before the pivot month.
						filteredMonths = months.filter(
							(month) => monthOrder[month] < monthOrder[pivotMonth],
						);
					}
				}
				return [year, filteredMonths] as [string, string[]];
			})
			// Exclude tuples with no months after filtering.
			.filter(([, months]) => months.length > 0)
	);
}
